/**
 * Runtime configuration from Vite environment variables. Only public values are allowed here:
 * the API URL, Cognito user pool and app client IDs, and origin are all shipped to browsers.
 */
function trimSlash(value: string): string {
  return value.replace(/\/+$/, '')
}

/**
 * Each variable is read by name: Vite then inlines only these values. Passing the whole
 * `import.meta.env` object would embed every VITE_* variable from local env files.
 */
const env: Partial<ImportMetaEnv> = {
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  VITE_COGNITO_REGION: import.meta.env.VITE_COGNITO_REGION,
  VITE_COGNITO_USER_POOL_ID: import.meta.env.VITE_COGNITO_USER_POOL_ID,
  VITE_COGNITO_CLIENT_ID: import.meta.env.VITE_COGNITO_CLIENT_ID,
  VITE_PUBLIC_ORIGIN: import.meta.env.VITE_PUBLIC_ORIGIN,
  VITE_AUTH_MODE: import.meta.env.VITE_AUTH_MODE,
}
const dev = import.meta.env.DEV

export type AuthMode = 'cognito' | 'local'

export interface AppConfig {
  apiBaseUrl: string
  cognito: { region: string; userPoolId: string; clientId: string }
  publicOrigin: string
  /** `local` is the development server's fake sign-in; production builds never accept it. */
  authMode: AuthMode
}

export function readConfig(values: Partial<ImportMetaEnv>, dev: boolean): AppConfig {
  return {
    apiBaseUrl: trimSlash(values.VITE_API_BASE_URL?.trim() ?? ''),
    cognito: {
      region: values.VITE_COGNITO_REGION?.trim() ?? '',
      userPoolId: values.VITE_COGNITO_USER_POOL_ID?.trim() ?? '',
      clientId: values.VITE_COGNITO_CLIENT_ID?.trim() ?? '',
    },
    publicOrigin: trimSlash(
      values.VITE_PUBLIC_ORIGIN?.trim() || (globalThis.location?.origin ?? ''),
    ),
    authMode: dev && values.VITE_AUTH_MODE === 'local' ? 'local' : 'cognito',
  }
}

function isHttpOrigin(value: string, allowHttp: boolean): boolean {
  try {
    const url = new URL(value)
    const loopback = url.hostname === 'localhost' || url.hostname === '127.0.0.1'
    return url.protocol === 'https:' || (allowHttp && url.protocol === 'http:' && loopback)
  } catch {
    return false
  }
}

/**
 * Human-readable problems with a configuration, naming the variable to fix. Empty when the
 * owner pages can work; participation needs only the API URL.
 */
export function configProblems(
  values: Partial<ImportMetaEnv>,
  current: AppConfig,
  dev: boolean,
): string[] {
  const problems: string[] = []
  const placeholder = (value: string) => value.startsWith('replace-with')
  if (!current.apiBaseUrl || placeholder(current.apiBaseUrl)) {
    problems.push('Set VITE_API_BASE_URL to the API Gateway URL.')
  } else if (!isHttpOrigin(current.apiBaseUrl, dev)) {
    problems.push('VITE_API_BASE_URL must be an https:// URL.')
  }
  if (values.VITE_AUTH_MODE === 'local' && !dev) {
    problems.push('VITE_AUTH_MODE=local works only on the development server.')
  }
  if (current.authMode === 'local') return problems

  const { region, userPoolId, clientId } = current.cognito
  if (!region || !userPoolId || !clientId || [region, userPoolId, clientId].some(placeholder)) {
    problems.push(
      'Set VITE_COGNITO_REGION, VITE_COGNITO_USER_POOL_ID, and VITE_COGNITO_CLIENT_ID (`npm run aws:frontend-env` prints them).',
    )
    return problems
  }
  if (!/^[a-z]{2}(-[a-z]+)+-\d$/.test(region)) {
    problems.push('VITE_COGNITO_REGION must be an AWS region such as us-east-1.')
  } else if (!/^[\w-]+_[0-9A-Za-z]+$/.test(userPoolId) || !userPoolId.startsWith(`${region}_`)) {
    problems.push('VITE_COGNITO_USER_POOL_ID must be a user pool ID in VITE_COGNITO_REGION.')
  }
  if (!/^[0-9a-z]+$/.test(clientId)) {
    problems.push('VITE_COGNITO_CLIENT_ID must be the app client ID (letters and digits).')
  }
  return problems
}

export const config = readConfig(env, dev)

export const configurationProblems = configProblems(env, config, dev)

/** True when owner sign-in and the API are configured. */
export const isBackendConfigured = configurationProblems.length === 0
